import { Formik } from 'formik';
import * as Yup from 'yup';
import { editTeam, getAccountProfile } from '../../../api/requests';
import Page from '../../../components/PageWrapper';
import Button from '../../../components/form-control/Button';
import Input from '../../../components/form-control/Input';
import { getPhoneNumberFromShortCodeFormat, getFormattedPhoneNumber, phoneValidationSchema, showToast } from '../../../utils/miscFunctions';
import { useState } from 'react';
import useUserAccount from '../../../hooks/userAccountHook';
import Loader from '../../../components/Loader';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import FileUpload from '../../../components/elements/FileUpload/FileUpload';
import { UPLOAD_IMAGE_ENDPOINTS, UPLOADABLE_DOCUMENT_TYPES } from '../../../resources/enums';
import useImage from '../../../hooks/imageHook';

const BusinessPreference = () => {
  const { setProfile, user, updateProfile } = useUserAccount();
  const [profileData, setProfileData] = useState<any>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const invalidateQueries = useQueryClient();
  const [profileImage, setProfileImage] = useState<any>();
  const { uploadStaffImage } = useImage();

  let teamID: string = user?.company?.id ?? '';


  const getProfileDetails = async () => {
    try {
      const response = await getAccountProfile();
      if (response?.data) {
        setProfile(response.data);
        updateProfile(response?.data);
        setProfileData(response.data);
      }
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to fetch profile details');
    }
  };

  const uploadStaffPhoto = async () => {
    try {
      const response: any = await uploadStaffImage(profileImage, UPLOADABLE_DOCUMENT_TYPES.PASSPORT_PHOTO, UPLOAD_IMAGE_ENDPOINTS.COMPANY)
      if (response) {
        showToast.success(response?.message ?? 'Profile photo uploaded successfully');
        invalidateQueries.invalidateQueries({ queryKey: ['getProfileDetails'] });

      }
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to upload profile photo');
    }
  };


  const disableFormEdit = (): boolean => !user.isOwner();

  const { isLoading: profileDataLoading } = useQuery({
    queryKey: ['getProfileDetails', teamID, profileData],
    queryFn: () => getProfileDetails()
  });


  return (
    <Page title="Business Preference">
      <div className="min-h-full bg-white dark:bg-darkBg w-full">
        {profileDataLoading || !profileData ? (
          <Loader message="Fetching organization details. Please wait..." brollyLoaderFixed />
        ) : (
          <div className="bg-white dark:bg-darkBg">
            <Formik
              initialValues={{
                companyName: profileData?.company?.name,
                companyEmail: profileData?.company?.email,
                companyAddress: profileData?.company?.address,
                corporatePhone: getPhoneNumberFromShortCodeFormat(profileData?.company?.corporatePhone),
                mobilePhone: getPhoneNumberFromShortCodeFormat(profileData?.company?.mobilePhone),
                nicRegistrationNumber: profileData?.company?.nicRegistrationNumber || undefined,
              }}
              validateOnBlur
              validationSchema={Yup.object().shape({
                companyName: Yup.string()
                  .min(2, 'Company name must be at least 2 characters.')
                  .max(50, 'Company name cannot exceed 50 characters.')
                  .required('Please enter the company name.'),
                companyEmail: Yup.string().email('Please enter a valid email address.').required('Please enter the company email.'),
                companyAddress: Yup.string()
                  .min(2, 'Company address must be at least 2 characters.')
                  .max(50, 'Company address cannot exceed 50 characters.')
                  .required('Please enter the company address.')
                  .nullable(),
                corporatePhone: phoneValidationSchema(),
                mobilePhone: phoneValidationSchema(),
              })}
              onSubmit={(values) => {
                setIsSubmitting(true);

                const companyInformationPayload = {
                  name: values.companyName,
                  email: values.companyEmail,
                  address: values.companyAddress,
                  corporatePhone: getFormattedPhoneNumber(values.corporatePhone),
                  mobilePhone: getFormattedPhoneNumber(values.mobilePhone),
                  type: user.company?.type,
                  countryName: profileData?.company?.countryName,
                  nicRegistrationNumber: values.nicRegistrationNumber

                };

                editTeam(teamID, companyInformationPayload)
                  .then((response) => {
                    if (response) {
                      profileImage && uploadStaffPhoto();
                      showToast.success(response?.message ?? 'Business details updated successfully');
                      invalidateQueries.invalidateQueries({ queryKey: ['getProfileDetails'] });
                    }
                  })
                  .catch((error) => {
                    showToast.error(error?.description ?? 'Failed to update organization details');
                    setIsSubmitting(false);
                  })
                  .finally(() => {
                    setIsSubmitting(false);
                  });
              }}>
              {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, errors, touched }) => {
                return (
                  <form onSubmit={handleSubmit}>

                    {profileData && (
                      <div className="px-5">

                        <FileUpload
                          onFileLoad={(images: any) => {
                            if (images && images[0]) {
                              setProfileImage(images[0]);
                            }
                          }}
                          title="Upload Logo"
                          onFileDeleted={() => {
                            setProfileImage({
                              profileImage: null
                            });
                          }}
                          type="image"
                          showFileMax={false}
                          savedImage={profileData?.company?.logo}
                          profilePhoto
                          isProfile
                          subtitle="Upload a logo for your organization"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[24.5px] items-center justify-center p-4">
                      <Input
                        type="text"
                        name="companyName"
                        label="Company Name"
                        value={values.companyName}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        required
                        disabled={disableFormEdit()}
                        error={touched.companyName ? errors.companyName : undefined}
                        helperText={touched?.companyName ? errors.companyName : undefined}
                      />

                      <Input
                        type="email"
                        name="companyEmail"
                        label="Email"
                        value={values.companyEmail}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        required
                        disabled={disableFormEdit()}
                        error={touched.companyEmail ? errors.companyEmail : undefined}
                        helperText={touched?.companyEmail ? errors.companyEmail : undefined}
                      />

                      <div className="md:col-span-2">
                        <Input
                          name="companyAddress"
                          label="Address"
                          value={values.companyAddress}
                          onChange={handleChange}
                          onBlur={handleChange}
                          placeholder="Eg: 40 Awudu Street, Accra"
                          error={touched.companyAddress ? errors.companyAddress : undefined}
                          helperText={
                            touched?.companyAddress ? errors.companyAddress : undefined
                          }
                          required
                          disabled={disableFormEdit()}
                        />
                      </div>

                      <Input
                        type="tel"
                        name="corporatePhone"
                        label="Corporate Phone"
                        value={values.corporatePhone}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.corporatePhone ? errors.corporatePhone : undefined}
                        helperText={
                          touched?.corporatePhone ? errors.corporatePhone : undefined
                        }
                        disabled={disableFormEdit()}
                      />

                      <Input
                        type="tel"
                        name="mobilePhone"
                        label="Mobile Phone"
                        value={values.mobilePhone}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.mobilePhone ? errors.mobilePhone : undefined}
                        helperText={touched?.mobilePhone ? errors.mobilePhone : undefined}
                        disabled={disableFormEdit()}
                      />
                    </div>

                    <div className='px-5'>
                      <Input
                        type="text"
                        name="nicRegistrationNumber"
                        label="NIC Registration Number"
                        value={values.nicRegistrationNumber}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.nicRegistrationNumber ? errors.nicRegistrationNumber : undefined}
                        helperText={touched?.nicRegistrationNumber ? errors.nicRegistrationNumber : undefined}
                        disabled={disableFormEdit()}
                      />
                    </div>


                    {!disableFormEdit() && (
                      <div className="flex justify-end mt-10">
                        <Button
                          label="Save"
                          type="submit"
                          className="px-[18px] py-[12px] h-[45px] "
                          bgColor="primary"
                          size="lg"
                          curved
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                  </form>
                );
              }}
            </Formik>
          </div>
        )}
      </div>
    </Page>
  );
};

export default BusinessPreference;
